import { OverlayRef } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { TopNotificationService } from '@shared/services/notification/top-notification.service';
import { PopoverService } from '@widgets/popover/popover.service';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { QuickSearchResult, SearchCategoryEnum, SearchResult } from './data/quick-access.model';
import { QuickAccessService } from './services/quick-access.service';

export enum KeyboardKeys {
  UP = 'ArrowUp',
  DOWN = 'ArrowDown',
  ENTER = 'Enter',
}

@Directive()
export abstract class QuickSearchBaseComponent implements OnInit, OnDestroy {
  @ViewChild('dialogTemplate') dialogTemplate!: TemplateRef<unknown>;

  @Output() popoverVisible$ = new EventEmitter();

  popoverVisible: boolean = false;
  searchInProgress: boolean;
  minCharsReached = false;
  numOfMinCharsToStartSearch: number = 4;

  currentResults: QuickSearchResult;
  selectedResult: SearchResult;
  inititalResults: QuickSearchResult = {
    chargepointResults: 0,
    chargingStationResults: 0,
    searchResults: [],
  };
  searchTerms$ = new Subject<string>();

  protected readonly popoverService: PopoverService = inject(PopoverService);
  protected readonly vcr: ViewContainerRef = inject(ViewContainerRef);
  protected overlayRef: OverlayRef | null = null;
  protected unsubscribe$ = new Subject<void>();

  protected constructor(
    protected quickAccessService: QuickAccessService,
    protected topNotificationService: TopNotificationService,
    protected cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.resetSearchInputOnRouteChange();
    this.handleSearchTermChange();
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.popoverService.close(this.overlayRef);
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  openDialog() {
    if (this.overlayRef) return;

    this.overlayRef = this.popoverService.open({
      origin: this.originPopover().nativeElement,
      content: this.dialogTemplate,
      vcr: this.vcr,
      hasBackdrop: false,
      individualHeight: true,
    });

    this.popoverVisible = true;

    setTimeout(() => {
      this.searchInput()?.nativeElement.focus();
    });

    this.overlayRef.detachments().subscribe(() => {
      this.overlayRef = null;
      this.popoverVisible = false;
    });
  }

  closeDialog(): void {
    if (this.overlayRef) {
      this.popoverService.close(this.overlayRef);
      this.overlayRef = null;
    }
    this.popoverVisible = false;
    this.cdr.detectChanges();
  }

  abstract originPopover(): ElementRef<HTMLElement>;

  abstract searchInput(): ElementRef<HTMLInputElement>;

  abstract selected(element: SearchResult): void;

  // only for header menu
  protected abstract resetSearchInputOnRouteChange(): void;

  onKeyUpWithinInput(event: KeyboardEvent, inputSearchTerm: string): void {
    event.stopImmediatePropagation();

    if (this.minCharsReached && !this.searchInProgress) {
      this.handleArrowNavigation(event);
    }

    const searchTerm = (inputSearchTerm || this.searchInput()?.nativeElement.value)?.trim();
    this.minCharsReached = searchTerm?.length >= this.numOfMinCharsToStartSearch;
    this.searchTerms$.next(searchTerm);
  }

  setPopoverVisibility(isVisible: boolean): void {
    if (isVisible && this.popoverVisible) {
      // Already visible, don't toggle
      return;
    }
    if (!isVisible && !this.popoverVisible) {
      // Already hidden, don't toggle
      return;
    }

    if (isVisible) {
      this.openDialog();
    }
    this.popoverVisible = isVisible;
    this.popoverVisible$.emit(this.popoverVisible);
  }

  onKeyDownWithinInput(event: KeyboardEvent): void {
    if (event.key === KeyboardKeys.DOWN || event.key === KeyboardKeys.UP) {
      event.preventDefault();
    }
  }

  protected handleSearchTermChange(): void {
    this.searchTerms$
      .pipe(
        distinctUntilChanged(),
        tap(() => (this.searchInProgress = true)),
        debounceTime(500),
        switchMap(term => {
          return term && this.minCharsReached
            ? this.quickAccessService.quickSearch(term).pipe(
                catchError(error => {
                  this.searchInProgress = false;
                  this.topNotificationService.showErrorInTopNotification(error);
                  return of<QuickSearchResult>(this.inititalResults);
                })
              )
            : of<QuickSearchResult>(this.inititalResults);
        }),
        startWith(this.inititalResults),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((results: QuickSearchResult) => {
        const chargePointsResults = results?.searchResults
          .filter(elm => elm.category === SearchCategoryEnum.CHARGEPOINT)
          .sort(this.sortByValue);
        const chargingStationsResults = results?.searchResults
          .filter(elm => elm.category === SearchCategoryEnum.CHARGINGSTATION)
          .sort(this.sortByValue);
        this.currentResults = {
          chargepointResults: chargePointsResults.length,
          chargingStationResults: chargingStationsResults.length,
          searchResults: [...chargingStationsResults, ...chargePointsResults],
        };
        this.selectedResult = this.currentResults.searchResults[0];
        this.searchInProgress = false;
        this.cdr.detectChanges();
      });
  }

  private handleArrowNavigation(event: KeyboardEvent): void {
    let index = this.selectedResult
      ? this.currentResults.searchResults.findIndex(elm => elm.value === this.selectedResult.value)
      : -1;
    if (event.key === KeyboardKeys.DOWN) {
      index = this.mod(index + 1, this.currentResults.searchResults.length);
      this.selectedResult = this.currentResults.searchResults[index];
      return;
    }
    if (event.key === KeyboardKeys.UP) {
      index = this.mod(index - 1, this.currentResults.searchResults.length);
      this.selectedResult = this.currentResults.searchResults[index];
      return;
    }
    if (event.key === KeyboardKeys.ENTER && this.selectedResult) {
      this.selected(this.selectedResult);
      return;
    }
  }

  /**
   * This is needed because default javascript '%' operator
   * cannot handle negative values as expected here.
   *
   * e.g. -1 % 3 returns -1 instead of 2
   */
  private mod(a: number, b: number): number {
    return ((a % b) + b) % b;
  }

  private sortByValue(a: SearchResult, b: SearchResult): number {
    if (!!a && !!a.value && !!b && !!b.value) {
      return a.value.localeCompare(b.value);
    }
    return 0;
  }
}
